import type {
  AgreementResponse,
  MerchantTaxSettings,
  OrganizationMetadataRequirements,
  ProductResponse,
  TaxRateRead,
  TaxRegistration
} from '~/data/api/client/billing'
import routeDirectory from '~/navigation/routes'

import {
  type BillingCompletenessKey,
  type CompletenessMessageKey,
  completenessHref,
  completenessMessageKey
} from './completenessItems'
import type { BillingCompletenessStatus } from './types'

/**
 * Client-side "can I bill customers yet?" checklist. Uses the same item keys as the assistant's
 * `get_billing_setup_overview` tool, so the panel can show a score before (and without) a model turn
 * and the billing hub can surface it as a card.
 */

export type BillingSetupItem = {
  key: BillingCompletenessKey
  status: BillingCompletenessStatus
  /** Leaf key under `features.BillingComposer`. */
  messageKey: CompletenessMessageKey
  href: string
}

export type BillingSetupCompleteness = {
  /** 0–100; `done` items count fully, `warning` items half. */
  score: number
  items: BillingSetupItem[]
}

export type BillingSetupInput = {
  products: ProductResponse[]
  agreements: AgreementResponse[]
  /** Omit the tax inputs entirely (undefined) when tax management is not enabled for the org. */
  taxSettings?: MerchantTaxSettings | null
  taxRates?: TaxRateRead[]
  registrations?: TaxRegistration[]
  requirements?: OrganizationMetadataRequirements | null
  /** `undefined` when unknown (not fetched); the item is then omitted. */
  stripeConnectStatus?: 'connected' | 'missing'
}

export function computeBillingSetupCompleteness(input: BillingSetupInput): BillingSetupCompleteness {
  const activeProducts = input.products.filter((product) => product.status !== 'ARCHIVED')
  const liveAgreements = input.agreements.filter((agreement) => agreement.status !== 'ARCHIVED')
  const items: BillingSetupItem[] = []

  items.push(item('products.active', activeProducts.length > 0 ? 'done' : 'todo'))

  const withoutPricing = activeProducts.filter((product) => !hasPricing(product))
  items.push(
    item(
      'products.pricing',
      activeProducts.length === 0 ? 'todo' : withoutPricing.length === 0 ? 'done' : 'warning',
      withoutPricing[0] ? routeDirectory.settings.products.edit(withoutPricing[0].id) : undefined
    )
  )

  const needingAssets = activeProducts.filter(needsEntitySelections)
  const withoutAssets = needingAssets.filter((product) => (product.entitySelections?.length ?? 0) === 0)
  items.push(
    item(
      'products.entitySelections',
      activeProducts.length === 0 ? 'todo' : withoutAssets.length === 0 ? 'done' : 'warning',
      withoutAssets[0] ? routeDirectory.settings.products.edit(withoutAssets[0].id) : undefined
    )
  )

  items.push(item('agreements.active', liveAgreements.length > 0 ? 'done' : 'todo'))

  const agreementsWithProducts = liveAgreements.filter(
    (agreement) => (agreement.products ?? []).some((assignment) => assignment.isActive !== false)
  )
  const emptyAgreement = liveAgreements.find((agreement) => !agreementsWithProducts.includes(agreement))
  items.push(
    item(
      'agreements.products',
      agreementsWithProducts.length > 0 ? 'done' : liveAgreements.length > 0 ? 'warning' : 'todo',
      liveAgreements.length > 0 && agreementsWithProducts.length === 0 && emptyAgreement?.id
        ? routeDirectory.settings.agreements.edit(emptyAgreement.id)
        : routeDirectory.settings.agreements.new
    )
  )

  if (input.taxSettings !== undefined) {
    const settings = input.taxSettings
    items.push(item('tax.origin', settings?.originCountryCode ? 'done' : 'todo'))
    items.push(item('tax.defaultCode', settings?.defaultTaxCode ? 'done' : 'todo'))
    items.push(item('tax.defaultBehavior', settings?.defaultTaxBehavior ? 'done' : 'todo'))
  }

  if (input.taxRates !== undefined || input.registrations !== undefined) {
    const rateCount = input.taxRates?.length ?? 0
    const registrationCount = (input.registrations ?? []).filter((registration) => registration.active !== false).length
    // Manual rates and Stripe Tax registrations are alternative ways to cover sales tax: either one covers both items.
    const covered = rateCount + registrationCount > 0
    items.push(item('tax.rates', covered ? 'done' : 'todo'))
    items.push(item('tax.registrations', covered ? 'done' : 'todo'))
  }

  if (input.requirements) {
    const productKeys = input.requirements.requiredProductMetadataKeys ?? []
    const agreementKeys = input.requirements.requiredAgreementMetadataKeys ?? []
    const productsMissing = activeProducts.filter((product) => missingKeys(product.metadata, productKeys))
    const agreementsMissing = liveAgreements.filter((agreement) => missingKeys(agreement.metadata, agreementKeys))
    const missing = productsMissing.length + agreementsMissing.length
    const firstProduct = productsMissing[0]
    const firstAgreement = agreementsMissing[0]
    items.push(
      item(
        'products.requiredMetadata',
        productKeys.length + agreementKeys.length === 0 || missing === 0 ? 'done' : 'warning',
        firstProduct
          ? routeDirectory.settings.products.edit(firstProduct.id)
          : firstAgreement?.id
            ? routeDirectory.settings.agreements.edit(firstAgreement.id)
            : undefined
      )
    )
  }

  if (input.stripeConnectStatus) {
    items.push(item('billing.stripeConnect', input.stripeConnectStatus === 'connected' ? 'done' : 'todo'))
  }

  return { score: scoreOf(items), items }
}

function item(key: BillingCompletenessKey, status: BillingCompletenessStatus, href?: string): BillingSetupItem {
  return { key, status, messageKey: completenessMessageKey(key, status), href: href ?? completenessHref(key) }
}

function scoreOf(items: BillingSetupItem[]): number {
  if (items.length === 0) return 0
  const points = items.reduce((sum, current) => sum + (current.status === 'done' ? 1 : current.status === 'warning' ? 0.5 : 0), 0)
  return Math.round((points / items.length) * 100)
}

function hasPricing(product: ProductResponse): boolean {
  return Boolean(
    product.flatRatePricing ||
      product.perUnitPricing ||
      product.timeOfUsePricing ||
      product.volumePricing ||
      product.tariffPassThroughPricing ||
      (product.prices?.length ?? 0) > 0
  )
}

/**
 * Usage-priced PRIVATE / PUBLIC products bill sessions on specific assets, so an empty selection means
 * nothing can ever be rated. Flat subscriptions and marketplace offerings are attached via agreements instead.
 */
function needsEntitySelections(product: ProductResponse): boolean {
  if (product.visibility !== 'PRIVATE' && product.visibility !== 'PUBLIC') return false
  if (product.flatRatePricing || product.pricingType === 'FLAT_RATE') return false
  return true
}

function missingKeys(metadata: Record<string, string> | undefined, required: string[]): boolean {
  if (required.length === 0) return false
  return required.some((key) => !metadata?.[key]?.trim())
}
